import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Calendar,
  Clock,
  Plane,
  Users,
  Luggage,
  MapPin,
  User as UserIcon,
  Mail,
  Phone,
  Car,
  CheckCircle2,
  CreditCard,
  Info,
  FileText,
  ShieldCheck,
} from "lucide-react";
import PublicAIChatWidget from "@/components/public/PublicAIChatWidget";
import LanguageSwitcher from "@/components/public/LanguageSwitcher";
import AddressAutocomplete from "@/components/public/AddressAutocomplete";
import { useLang } from "@/lib/i18n";
import { MapContainer, TileLayer, Marker, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const pickIcon = L.divIcon({
  html: '<div style="background:#0A0A0A;color:#fff;border:2px solid #fff;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:10px;box-shadow:0 1px 4px rgba(0,0,0,.4)">A</div>',
  className: "", iconSize: [26,26], iconAnchor: [13,13],
});
const dropIcon = L.divIcon({
  html: '<div style="background:#F5B301;color:#0A0A0A;border:2px solid #0A0A0A;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:10px;box-shadow:0 1px 4px rgba(0,0,0,.4)">B</div>',
  className: "", iconSize: [26,26], iconAnchor: [13,13],
});

const VEHICLES = [
  { key: "standard", label: "Standard", pax: 3, luggage: 3, desc: "Sedán ejecutivo" },
  { key: "minivans", label: "Mini Van", pax: 7, luggage: 7, desc: "Furgoneta 7 plazas" },
  { key: "minibus", label: "Mini Bus", pax: 14, luggage: 14, desc: "Autobús pequeño" },
];

function defaultBookingDateTime() {
  // Ensure default is always > 24h from now (add 25h + round to next hour).
  const d = new Date(Date.now() + 25 * 3600 * 1000);
  d.setMinutes(0, 0, 0);
  const date = d.toISOString().slice(0, 10);
  const hh = String(d.getHours()).padStart(2, "0");
  return { date, time: `${hh}:00` };
}

function minBookingDate() {
  const d = new Date();
  d.setDate(d.getDate() + 1); // at least 24h in future → tomorrow
  return d.toISOString().slice(0, 10);
}

export default function PublicBookingPage() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    pickup_date: minBookingDate(),
    pickup_time: "10:00",
    pickup_location: "",
    dropoff_location: "",
    passengers: 1,
    luggage: 1,
    vehicle_type: "standard",
    flight_number: "",
    notes: "",
  });
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null); // {distance_km, suggested_price, ...}
  const [estimateErr, setEstimateErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null); // {booking_number, price, ...}
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Compute selected vehicle
  const vehicle = useMemo(
    () => VEHICLES.find((v) => v.key === form.vehicle_type) || VEHICLES[0],
    [form.vehicle_type]
  );

  // Capacity error
  const capacityError = useMemo(() => {
    if (form.passengers > vehicle.pax) {
      const label = t(vehicle.label);
      return t("El {vehicle} admite máximo {n} pasajero(s). Elige otro vehículo.")
        .replace("{vehicle}", label)
        .replace("{n}", vehicle.pax);
    }
    if (form.luggage > vehicle.luggage) {
      const label = t(vehicle.label);
      return t("El {vehicle} admite máximo {n} maleta(s). Elige otro vehículo.")
        .replace("{vehicle}", label)
        .replace("{n}", vehicle.luggage);
    }
    return "";
  }, [form.passengers, form.luggage, vehicle, t]);

  // Auto-estimate price whenever we have both locations + vehicle (debounced)
  useEffect(() => {
    if (!form.pickup_location.trim() || !form.dropoff_location.trim()) {
      setEstimate(null);
      setEstimateErr("");
      return;
    }
    const t = setTimeout(async () => {
      setEstimating(true);
      setEstimateErr("");
      try {
        const { data } = await axios.post(`${API}/public/estimate-price`, {
          pickup_location: form.pickup_location,
          dropoff_location: form.dropoff_location,
          vehicle_type: form.vehicle_type,
        });
        setEstimate(data);
      } catch (e) {
        setEstimate(null);
        setEstimateErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      } finally {
        setEstimating(false);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [form.pickup_location, form.dropoff_location, form.vehicle_type]);

  const validateStep1 = () => {
    if (!form.pickup_location.trim() || !form.dropoff_location.trim()) {
      toast.error(t("Rellena lugar de recogida y destino"));
      return false;
    }
    if (!form.pickup_date || !form.pickup_time) {
      toast.error(t("Fecha y hora son obligatorias"));
      return false;
    }
    const dt = new Date(`${form.pickup_date}T${form.pickup_time}`);
    if (dt.getTime() - Date.now() < 24 * 3600 * 1000) {
      toast.error(t("La recogida debe ser al menos 24 h después de ahora"));
      return false;
    }
    if (form.passengers < 1) {
      toast.error(t("Mínimo 1 pasajero"));
      return false;
    }
    if (capacityError) {
      toast.error(capacityError);
      return false;
    }
    if (!estimate && !estimating) {
      toast.error(t("No se pudo calcular el precio. Revisa las direcciones."));
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (submitting) return;
    if (!form.client_name.trim() || !form.client_email.trim() || !form.client_phone.trim()) {
      toast.error(t("Rellena tu nombre, email y teléfono"));
      return;
    }
    if (capacityError) {
      toast.error(capacityError);
      return;
    }
    if (!acceptedTerms) {
      toast.error(t("Debes aceptar los términos y condiciones"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/book`, form);
      setBooking(data);
      setStep(4);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const validateStep2 = () => {
    if (!form.client_name.trim() || !form.client_email.trim() || !form.client_phone.trim()) {
      toast.error(t("Rellena tu nombre, email y teléfono"));
      return false;
    }
    return true;
  };

  const payNow = async () => {
    if (!booking) return;
    try {
      const { data } = await axios.post(`${API}/public/services/checkout`, {
        booking_number: booking.booking_number,
        phone: form.client_phone,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="public-booking-page">
      <header className="relative bg-black text-white overflow-hidden">
        <img
          src="https://customer-assets-lqy194kg.emergentagent.net/job_complaint-tracker-102/artifacts/nanbffsr_images-2.jpeg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black" />
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm hover:text-[#F5B301]">
            <ArrowLeft size={16} /> {t("Volver")}
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher variant="light" />
            <div className="font-display font-bold tracking-tight text-lg">DS TRAVEL TOURS</div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 pb-8 relative z-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F5B301]">{t("Reserva online")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            {t("Reserva tu traslado en Madrid")}
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-8 text-[10px] uppercase tracking-widest flex-wrap">
          {[
            { n: 1, label: t("Trayecto") },
            { n: 2, label: t("Datos") },
            { n: 3, label: t("Resumen") },
            { n: 4, label: t("Pago") },
          ].map((s, i) => (
            <div key={s.n} className="flex items-center gap-2">
              <div
                className={`w-7 h-7 flex items-center justify-center border-2 border-black font-bold ${
                  step >= s.n ? "bg-black text-white" : "bg-white text-black"
                }`}
              >
                {step > s.n ? <CheckCircle2 size={14} /> : s.n}
              </div>
              <span className={step >= s.n ? "text-black" : "text-zinc-400"}>{s.label}</span>
              {i < 3 && <div className="w-6 h-px bg-zinc-300 mx-1" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <section className="bg-white border-2 border-black p-6 sm:p-8 space-y-6">
            <h1 className="font-display text-3xl font-bold tracking-tight">{t("Reserva tu trayecto")}</h1>

            {/* Locations */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t("Lugar de recogida")} icon={MapPin}>
                <AddressAutocomplete
                  value={form.pickup_location}
                  onChange={(v) => set("pickup_location", v)}
                  placeholder={t("Ej. Aeropuerto Madrid Barajas T4")}
                  testId="book-pickup-input"
                />
              </Field>
              <Field label={t("Lugar de entrega")} icon={MapPin}>
                <AddressAutocomplete
                  value={form.dropoff_location}
                  onChange={(v) => set("dropoff_location", v)}
                  placeholder={t("Ej. Hotel Riu Plaza España")}
                  testId="book-dropoff-input"
                />
              </Field>
            </div>

            {/* Date & time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label={t("Fecha (mín. 24h)")} icon={Calendar}>
                <Input
                  data-testid="book-date-input"
                  type="date"
                  min={minBookingDate()}
                  value={form.pickup_date}
                  onChange={(e) => set("pickup_date", e.target.value)}
                  className="h-11 rounded-none border-black"
                />
              </Field>
              <Field label={t("Hora")} icon={Clock}>
                <Input
                  data-testid="book-time-input"
                  type="time"
                  value={form.pickup_time}
                  onChange={(e) => set("pickup_time", e.target.value)}
                  className="h-11 rounded-none border-black"
                />
              </Field>
              <Field label={t("Número de vuelo (opcional)")} icon={Plane}>
                <Input
                  data-testid="book-flight-input"
                  value={form.flight_number}
                  onChange={(e) => set("flight_number", e.target.value.toUpperCase())}
                  placeholder="IB1234"
                  className="h-11 rounded-none border-black"
                />
              </Field>
            </div>

            {/* Vehicle selection */}
            <div>
              <Label className="text-[10px] uppercase tracking-widest">{t("Tipo de vehículo")}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                {VEHICLES.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => set("vehicle_type", v.key)}
                    data-testid={`book-vehicle-${v.key}`}
                    className={`text-left border-2 p-4 transition-colors ${
                      form.vehicle_type === v.key
                        ? "bg-black text-white border-black"
                        : "bg-white text-black border-black hover:bg-zinc-100"
                    }`}
                  >
                    <Car size={20} className="mb-2" />
                    <div className="font-display text-lg font-bold">{t(v.label)}</div>
                    <div className="text-xs opacity-70">{t(v.desc)}</div>
                    <div className="text-[10px] uppercase tracking-widest mt-2 opacity-80">
                      {v.pax} {t("pax")} · {v.luggage} {t("maletas")}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Passengers & luggage */}
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("Número de personas")} icon={Users}>
                <Input
                  data-testid="book-passengers-input"
                  type="number"
                  min="1"
                  max={vehicle.pax}
                  value={form.passengers}
                  onChange={(e) => set("passengers", parseInt(e.target.value) || 1)}
                  className="h-11 rounded-none border-black"
                />
              </Field>
              <Field label={t("Equipaje")} icon={Luggage}>
                <Input
                  data-testid="book-luggage-input"
                  type="number"
                  min="0"
                  max={vehicle.luggage}
                  value={form.luggage}
                  onChange={(e) => set("luggage", parseInt(e.target.value) || 0)}
                  className="h-11 rounded-none border-black"
                />
              </Field>
            </div>
            {capacityError && (
              <div
                className="text-sm bg-red-50 border-2 border-red-500 text-red-800 px-3 py-2 flex items-start gap-2"
                data-testid="book-capacity-error"
              >
                <Info size={14} className="mt-0.5" /> {capacityError}
              </div>
            )}

            {/* Notes */}
            <Field label={t("Notas (opcional — ej. silla de bebé, máx. 1 por vehículo)")}>
              <Textarea
                data-testid="book-notes-input"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder={t("Comentarios adicionales…")}
                rows={2}
                className="rounded-none border-black"
              />
            </Field>

            {/* Live price */}
            <PriceBox
              estimating={estimating}
              estimate={estimate}
              err={estimateErr}
              vehicle={vehicle}
              t={t}
            />

            {/* Mini-map so the client visually confirms the exact points */}
            <RouteMap estimate={estimate} t={t} />

            <Button
              type="button"
              disabled={estimating || !!capacityError || !estimate}
              onClick={() => validateStep1() && setStep(2)}
              className="w-full h-12 rounded-none btn-primary text-sm uppercase tracking-[0.25em]"
              data-testid="book-step1-next"
            >
              {t("Continuar")}
            </Button>
          </section>
        )}

        {step === 2 && (
          <section className="bg-white border-2 border-black p-6 sm:p-8 space-y-6">
            <h1 className="font-display text-3xl font-bold tracking-tight">{t("Tus datos")}</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t("Nombre completo")} icon={UserIcon}>
                <Input
                  data-testid="book-name-input"
                  value={form.client_name}
                  onChange={(e) => set("client_name", e.target.value)}
                  className="h-11 rounded-none border-black"
                />
              </Field>
              <Field label={t("Teléfono móvil")} icon={Phone}>
                <Input
                  data-testid="book-phone-input"
                  value={form.client_phone}
                  onChange={(e) => set("client_phone", e.target.value)}
                  placeholder="+34 600 000 000"
                  className="h-11 rounded-none border-black"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label={t("Correo electrónico")} icon={Mail}>
                  <Input
                    data-testid="book-email-input"
                    type="email"
                    value={form.client_email}
                    onChange={(e) => set("client_email", e.target.value)}
                    className="h-11 rounded-none border-black"
                  />
                </Field>
              </div>
            </div>

            <PriceBox estimating={false} estimate={estimate} vehicle={vehicle} t={t} />

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="h-12 rounded-none border-2 border-black text-xs uppercase tracking-widest"
                data-testid="book-step2-back"
              >
                <ArrowLeft size={14} className="mr-2" /> {t("Volver")}
              </Button>
              <Button
                type="button"
                onClick={() => validateStep2() && setStep(3)}
                className="flex-1 h-12 rounded-none btn-primary text-sm uppercase tracking-[0.25em]"
                data-testid="book-step2-next"
              >
                {t("Continuar")}
              </Button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section
            className="bg-white border-2 border-black p-6 sm:p-8 space-y-6"
            data-testid="book-summary-panel"
          >
            <div>
              <h1 className="font-display text-3xl font-bold tracking-tight">
                {t("Resumen de tu reserva")}
              </h1>
              <p className="text-sm text-zinc-600 mt-1">
                {t("Revisa los detalles antes de confirmar. Todos los impuestos están incluidos.")}
              </p>
            </div>

            <BookingSummary form={form} vehicle={vehicle} estimate={estimate} t={t} />

            {/* Visual confirmation of the exact points on the map */}
            <RouteMap estimate={estimate} t={t} />

            {/* Terms & Conditions */}
            <div className="border-2 border-black bg-zinc-50 p-5">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="accept-terms"
                  checked={acceptedTerms}
                  onCheckedChange={(v) => setAcceptedTerms(!!v)}
                  data-testid="book-accept-terms"
                  className="mt-1 border-black data-[state=checked]:bg-black data-[state=checked]:text-white"
                />
                <label htmlFor="accept-terms" className="text-sm cursor-pointer leading-relaxed">
                  {t("He leído y acepto los")}{" "}
                  <TermsDialog open={termsOpen} onOpenChange={setTermsOpen} t={t}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setTermsOpen(true);
                      }}
                      className="underline font-semibold hover:text-[#B45309]"
                      data-testid="book-view-terms"
                    >
                      {t("términos y condiciones")}
                    </button>
                  </TermsDialog>{" "}
                  {t("del servicio.")}
                </label>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(2)}
                className="h-12 rounded-none border-2 border-black text-xs uppercase tracking-widest"
                data-testid="book-step3-back"
              >
                <ArrowLeft size={14} className="mr-2" /> {t("Volver")}
              </Button>
              <Button
                type="button"
                disabled={submitting || !acceptedTerms}
                onClick={submit}
                className="flex-1 h-12 rounded-none btn-primary text-xs sm:text-sm uppercase tracking-[0.15em] sm:tracking-[0.25em]"
                data-testid="book-confirm-btn"
              >
                {submitting ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <>
                    <ShieldCheck size={16} className="mr-2" /> {t("Confirmar reserva")}
                  </>
                )}
              </Button>
            </div>
          </section>
        )}

        {step === 4 && booking && (
          <section
            className="bg-white border-2 border-black p-6 sm:p-8 space-y-6"
            data-testid="book-success-panel"
          >
            <div className="flex items-center gap-3 text-[#B45309]">
              <CreditCard size={28} />
              <div>
                <h1 className="font-display text-3xl font-bold tracking-tight">
                  {t("Reserva pendiente de pago")}
                </h1>
                <div className="text-sm text-zinc-600">
                  {t("Tu reserva está archivada. Para asignarte conductor, completa el pago.")}
                </div>
              </div>
            </div>

            <div className="border-2 border-black p-5 bg-zinc-50">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                {t("Número de reserva")}
              </div>
              <div
                className="font-display text-3xl font-bold tracking-tight"
                data-testid="book-booking-number"
              >
                {booking.booking_number}
              </div>
            </div>

            <BookingSummary form={form} vehicle={vehicle} estimate={estimate} priceOverride={booking.price} t={t} />

            <Button
              onClick={payNow}
              className="w-full h-12 rounded-none btn-primary text-sm uppercase tracking-[0.25em]"
              data-testid="book-pay-btn"
            >
              <CreditCard size={16} className="mr-2" /> {t("Pagar ahora")} {booking.price.toFixed(2)} €
            </Button>
            <p className="text-xs text-zinc-600 text-center">
              {t("También puedes pagar más tarde desde")}{" "}
              <Link to="/consulta" className="underline font-semibold">
                {t("\u201CConsultar mi reserva\u201D")}
              </Link>{" "}
              {t("con tu teléfono.")}
            </p>
            <div className="text-center">
              <button
                onClick={() => navigate("/")}
                className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black"
              >
                {t("Volver al inicio")}
              </button>
            </div>
          </section>
        )}
      </main>
      <PublicAIChatWidget />
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest flex items-center gap-1.5">
        {Icon && <Icon size={12} />} {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function PriceBox({ estimating, estimate, err, vehicle, t = (s) => s }) {
  if (err) {
    return (
      <div className="border-2 border-red-500 bg-red-50 p-4 text-sm text-red-800" data-testid="book-price-error">
        <div className="font-bold">{t("No se pudo calcular el precio")}</div>
        <div className="text-xs mt-1">{err}</div>
      </div>
    );
  }
  if (estimating) {
    return (
      <div className="border-2 border-black bg-zinc-50 p-4 flex items-center gap-3" data-testid="book-price-loading">
        <Loader2 className="animate-spin" size={18} />
        <span className="text-sm">{t("Calculando precio…")}</span>
      </div>
    );
  }
  if (!estimate) {
    return (
      <div className="border-2 border-dashed border-zinc-300 p-4 text-sm text-zinc-500" data-testid="book-price-empty">
        {t("Rellena origen y destino para ver el precio.")}
      </div>
    );
  }
  return (
    <div className="border-2 border-black bg-black text-white p-5" data-testid="book-price-box">
      <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{t("Precio total")}</div>
      <div className="flex items-end gap-3 flex-wrap">
        <div className="font-display text-4xl font-bold" data-testid="book-price-value">
          {estimate.suggested_price.toFixed(2)} €
        </div>
        <div className="text-xs opacity-70 pb-1">
          {t(vehicle.label)} · {estimate.distance_km} km ·{" "}
          {estimate.distance_km > 30 ? t("Cobro por km") : t("Precio base")}
        </div>
      </div>
      <div className="mt-2 text-xs opacity-70">
        {estimate.distance_km > 30
          ? `${estimate.price_per_km} €/km × ${estimate.distance_km} km (> 30 km)`
          : `${t("Precio base")} ${estimate.base_price} € (≤ 30 km)`}
      </div>
    </div>
  );
}


function BookingSummary({ form, vehicle, estimate, priceOverride, t = (s) => s }) {
  const total =
    priceOverride != null
      ? priceOverride
      : estimate?.suggested_price != null
      ? estimate.suggested_price
      : null;

  const rows = [
    { label: t("Concepto"), value: t("Traslado privado con conductor"), key: "concept" },
    { label: t("Fecha"), value: form.pickup_date, key: "date" },
    { label: t("Hora"), value: form.pickup_time, key: "time" },
    { label: t("Tipo de vehículo"), value: `${t(vehicle.label)} — ${t(vehicle.desc)}`, key: "vehicle" },
    { label: t("Lugar de recogida"), value: form.pickup_location, key: "pickup" },
    { label: t("Lugar de entrega"), value: form.dropoff_location, key: "dropoff" },
    { label: t("Vuelo"), value: form.flight_number || "—", key: "flight" },
    { label: t("Pasajeros"), value: `${form.passengers} ${t("pax")} · ${form.luggage} ${t("maleta(s)")}`, key: "pax" },
  ];

  return (
    <div className="border-2 border-black bg-white" data-testid="book-summary-card">
      <div className="bg-black text-white px-5 py-3 flex items-center gap-2">
        <FileText size={16} />
        <div className="text-[10px] uppercase tracking-widest font-bold">{t("Detalles del servicio")}</div>
      </div>
      <dl className="divide-y divide-zinc-200">
        {rows.map((r) => (
          <div key={r.key} className="flex gap-4 px-5 py-3 text-sm">
            <dt className="text-zinc-500 text-[10px] uppercase tracking-widest w-40 shrink-0 pt-0.5">
              {r.label}
            </dt>
            <dd className="flex-1 break-words" data-testid={`book-summary-${r.key}`}>
              {r.value}
            </dd>
          </div>
        ))}
      </dl>
      <div className="border-t-2 border-black bg-[#F5B301]/10 px-5 py-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-600">{t("Total de la reserva")}</div>
          <div className="text-xs text-zinc-700 mt-0.5">{t("Impuestos incluidos")}</div>
        </div>
        <div className="font-display text-3xl font-bold" data-testid="book-summary-total">
          {total != null ? `${total.toFixed(2)} €` : "—"}
        </div>
      </div>
    </div>
  );
}

const TERMS_KEYS = [
  "Al realizar la reserva, el cliente confirma que los datos proporcionados son correctos (fecha, hora, vuelo, hotel/dirección y número de pasajeros).",
  "Los cambios o cancelaciones deberán comunicarse con antelación.",
  "Todos los impuestos están incluidos en el precio mostrado. No se añadirán impuestos ni cargos adicionales durante el proceso de pago.",
  "El cliente deberá estar en el punto de recogida a la hora indicada y llevar consigo sus pertenencias. La empresa no se responsabiliza de objetos olvidados.",
  "Al confirmar la reserva, el cliente acepta estos términos y condiciones.",
];

function TermsDialog({ open, onOpenChange, children, t = (s) => s }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="max-w-lg rounded-none border-2 border-black"
        data-testid="book-terms-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {t("Términos y condiciones")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("Términos y condiciones")}
          </DialogDescription>
        </DialogHeader>
        <ol className="list-decimal pl-5 space-y-3 text-sm text-zinc-800 max-h-[60vh] overflow-y-auto pr-2">
          {TERMS_KEYS.map((k, i) => (
            <li key={i}>{t(k)}</li>
          ))}
        </ol>
      </DialogContent>
    </Dialog>
  );
}


function RouteMap({ estimate, t = (s) => s }) {
  if (!estimate?.pickup || !estimate?.dropoff) return null;
  const pu = [estimate.pickup.lat, estimate.pickup.lng];
  const dr = [estimate.dropoff.lat, estimate.dropoff.lng];
  const coords = estimate.geometry?.coordinates?.map((c) => [c[1], c[0]]);
  const bounds = coords && coords.length ? coords : [pu, dr];
  return (
    <div className="border-2 border-black bg-white overflow-hidden" data-testid="route-map">
      <div className="bg-black text-white px-4 py-2 flex items-center gap-2">
        <MapPin size={14} />
        <div className="text-[10px] uppercase tracking-widest font-bold">
          {t("Confirma los puntos exactos")}
        </div>
      </div>
      <MapContainer
        bounds={bounds}
        boundsOptions={{ padding: [24, 24] }}
        scrollWheelZoom={false}
        style={{ height: 260, width: "100%" }}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={pu} icon={pickIcon} />
        <Marker position={dr} icon={dropIcon} />
        {coords && coords.length > 1 && (
          <Polyline positions={coords} pathOptions={{ color: "#0A0A0A", weight: 4 }} />
        )}
      </MapContainer>
      <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest px-4 py-2 bg-zinc-50 border-t border-black">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-black inline-block" />
          {t("Recogida")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-[#F5B301] border border-black inline-block" />
          {t("Destino")}
        </span>
      </div>
    </div>
  );
}
